import { useStages } from "../hooks/useStages";
import { useTimer } from "../hooks/useTimer";
import "../App.css";
import { useState, useEffect, useCallback } from "react";
import { CountdownTimer } from "./CountdownTimer";
import { TimerGraphic } from "./TimerGraphic";
import { TimerButtons } from "./TimerButtons";
import { getNotification } from "../util/notification";

const Timer = () => {
  const [topOrigin, setTopOrigin] = useState(0);
  const [botOrigin, setBotOrigin] = useState(0);

  const [isFlipped, setIsFlipped] = useState(false);
  const [isSandFallingIntoPlace, setIsSandFallingIntoPlace] = useState(false);

  const {
    currentStage,
    currentStageIndex,
    currentStageLength,
    maxStageLength,
    isLastStage,
    goNextStage,
    goFirstStage,
    nextStage,
  } = useStages();

  const {
    remainingSeconds,
    elapsedSeconds,
    totalSeconds,
    isFinished,
    isPaused,
    resetTimer,
    pauseTimer,
    resumeTimer,
  } = useTimer(currentStageLength);

  const handleStageTransition = useCallback(async (shouldResume) => {
    // Flip the hourglass
    setIsFlipped((old) => !old);
    setIsSandFallingIntoPlace(false);

    // Wait for the flip to finish
    await new Promise((resolve) => setTimeout(resolve, 2000));

    goNextStage();
    pauseTimer();
    setIsSandFallingIntoPlace(true);

    // Re-position the sand elements
    setTopOrigin((old) => (old === 0 ? -48 : 0));
    setBotOrigin((old) => (old === 0 ? 50 : 0));

    // Wait for sand to fall
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Ensure sand doesn't keep animating!
    setIsSandFallingIntoPlace(false);

    if (shouldResume) {
      resumeTimer();
    }
  }, []);

  useEffect(() => {
    if (isFinished) {
      getNotification(currentStage, nextStage);
      const shouldResume = !isLastStage;
      handleStageTransition(shouldResume);
    }
    // purposefully exclude isLastStage from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  // top-facing half is proportionate to max stage length, bottom is not
  // const top = 39 * (1 - remainingSeconds / maxStageLength) + topOrigin;
  const bot = 37 * (1 - remainingSeconds / maxStageLength) + botOrigin;

  const remDivMax =
    (remainingSeconds + (maxStageLength - currentStageLength)) / maxStageLength;
  const top = 39 * (1 - remDivMax) + topOrigin;

  return (
    <div className="Timer-content">
      <div
        style={{
          marginTop: 42,
          fontSize: 42,
          width: "100vw",
          textAlign: "center",
          position: "absolute",
        }}
      >
        {isPaused && !isSandFallingIntoPlace ? "Pomodoro" : currentStage}
      </div>
      <div className="Timer-text">
        <CountdownTimer
          timerCount={currentStageLength}
          remainingSeconds={remainingSeconds}
          isFinished={isFinished}
          isPaused={isPaused}
          resetTimer={resetTimer}
          pauseTimer={pauseTimer}
          resumeTimer={resumeTimer}
        />
      </div>
      <TimerGraphic
        topOffset={top}
        bottomOffset={bot}
        remainingSeconds={remainingSeconds}
        timerCount={currentStageLength}
        isFlipped={isFlipped}
        isSandFallingIntoPlace={isSandFallingIntoPlace}
      />
      <TimerButtons
        isPaused={isPaused && !isSandFallingIntoPlace}
        pauseTimer={pauseTimer}
        resumeTimer={resumeTimer}
        resetTimer={resetTimer}
      />
    </div>
  );
};

export { Timer };
